import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { useApp, useBehavior, useSubscribeOnWsUpdate, useUpdateWidget, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import { currencyFormat, eventBus, significantDecimalPlaces } from '@libs/utils';
import React, { useEffect, useEffectEvent, useMemo } from 'react';
import { TBalanceData } from '../../types/data';
import { EConsumerLocalStorageItems } from '../../types/enums';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import SelectBalanceBottomSheet from './SelectBalanceBottomSheet';
import { getIconForCurrencyIso } from './utils/balanceUtils';

export const Balance: React.FC<TWidgetProps<TBalanceData>> = ({ widget }) => {
    const data = widget.data ?? {};
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const restoreSavedBalance = useEffectEvent(() => {
        const savedIso = localStorage.getItem(EConsumerLocalStorageItems.PREFERRED_BALANCE);
        if (savedIso) {
            if (Array.isArray(data.balances) && data.balances?.length > 0) {
                const currentBalance = data.balances.find((b) => b?.iso === savedIso);
                if (currentBalance?.id) {
                    updateWidgetValue(currentBalance.id);
                }
            }
        }
    });

    useEffect(() => {
        restoreSavedBalance();
    }, []);

    const { setPopup } = useApp();
    const { updateWidget } = useUpdateWidget(widget.uid);
    const { isHidden } = useBehavior(widget);

    const currentBalance = useMemo(() => {
        if (Array.isArray(data.balances) && data.balances?.length > 0) {
            const currentBalance = data.balances.find((b) => b?.id === data.value) ?? data.balances[0];
            return {
                currentBalance: currentBalance,
                showArrow: data.balances.length > 1 || data.canGetPaysendCard || data.canExchange || data.canAddCurrency,
            };
        }
        return null;
    }, [data.balances, data.canAddCurrency, data.canExchange, data.canGetPaysendCard, data.value]);

    const arrActions = useMemo(() => widget.actions?.find((a) => a?.actionType === ActionActionTypeEnum.onTimer), [widget.actions]);

    useSubscribeOnWsUpdate(arrActions);

    useEffect(() => {
        if (widget.internalId) {
            eventBus.subscribeWS(widget.internalId, (w) => {
                if (w) {
                    updateWidget(w as unknown as TWidget<TBalanceData>);
                }
            });
        }
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [updateWidget, widget.internalId]);

    const cb = currentBalance?.currentBalance;
    const hasCurrencyIcon = !!getIconForCurrencyIso(cb?.iso);

    if (!cb) {
        return false;
    }

    const decimalLength = significantDecimalPlaces(cb.balance) > 0 ? 2 : 0;

    const amountText = hasCurrencyIcon ? (currencyFormat(cb.balance, cb.iso, decimalLength) as string) : `${cb.balance} ${cb.iso?.toLowerCase()}`;

    const amountTextTransformation = hasCurrencyIcon
        ? undefined
        : [
              {
                  text: `${cb.balance}`,
                  precision: decimalLength,
                  useLocaleNumberFormat: true,
              },
              {
                  text: cb.iso?.toLowerCase(),
                  smallCaps: true,
              },
          ];
    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];

    return (
        <CoreLayoutView
            uid={widget.uid}
            key={cb.id}
            isHide={isHidden}
            styleName={widget.options?.style}
            styleProps={{
                orientation: ORIENTATION_ENUM.VERTICAL,
                lineX: 'center',
                gap: 8,
                padding: { top: 4, right: 8, bottom: 8, left: 8 },
                borderRadius: 12,
                margin: widget.options?.margin,
                hovered: { backgroundColor: 'var(--color-white)' },
                pressed: { backgroundColor: 'var(--color-white_40_tr_base)' },
            }}
            onClick={
                currentBalance.showArrow
                    ? () => {
                          setPopup(
                              <SelectBalanceBottomSheet
                                  parentStyleNames={parentStyleNames}
                                  value={cb.id}
                                  widget={widget}
                                  storageKey={EConsumerLocalStorageItems.PREFERRED_BALANCE}
                              />,
                          );
                      }
                    : undefined
            }
        >
            <CoreTextView
                uid={widget.uid + '_amount'}
                text={amountText}
                styleName="balanceWidgetAmount"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    ...TextStyle.display1,
                    main: 'var(--color-black)',
                    maxLines: 1,
                    textTransformation: amountTextTransformation,
                }}
            />
            <CoreLayoutView
                uid={widget.uid + '_currency'}
                styleName="balanceWidgetCurrencyRow"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    orientation: ORIENTATION_ENUM.HORIZONTAL,
                    lineX: 'center',
                    gap: 2,
                }}
            >
                <CoreTextView
                    text={cb.name}
                    styleName="balanceWidgetCurrencyName"
                    parentStyleNames={parentStyleNames}
                    styleProps={{
                        ...TextStyle.secondary,
                        main: 'var(--color-grey_72_lc)',
                    }}
                />
                {currentBalance.showArrow && (
                    <VisualView
                        iconName={AssetsSvgMap.ic_chevron_down_small}
                        size="xxSmall"
                        parentStyleNames={[...parentStyleNames, 'balanceWidgetIcon']}
                        iconColor="var(--color-grey_72_lc)"
                    />
                )}
            </CoreLayoutView>
        </CoreLayoutView>
    );
};
